import { useCallback, useEffect, useMemo, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import './SinglePropertyLayoutTab.css';
import EditFieldModal from './components/EditFieldModal';
import FieldModal from './components/FieldModal';
import PlaceholderLoader from './components/PlaceholderLoader/PlaceholderLoader';
import PropertyLayout from './components/PropertyLayout';

const SinglePropertyLayoutTab = ({ showToast, setIsLoading, onActionsReady, isSaving, isResetting }) => {
  // Memoize initialDynamicFields to prevent recreation
  const initialDynamicFields = useMemo(() => ({
    'gallery': [
      {
        id: 'carousel-default',
        type: 'carousel',
        label: __('Image Carousel', 'havenlytics')
      }
    ],
    'title': [],
    'tags': [],
    'details': [],
    'features': [],
    'agent_info': [],
    'amenities': [],
    'featured': [],
    'views': []
  }), []);

  const [dynamicFields, setDynamicFields] = useState(initialDynamicFields);
  const [activeModal, setActiveModal] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingArea, setEditingArea] = useState(null);
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false); // Add this state to track initial data load
  
  // Refs to prevent multiple operations and hold current state
  const dataLoadedRef = useRef(false);
  const isSavingRef = useRef(false);
  const isResettingRef = useRef(false);
  const dynamicFieldsRef = useRef(dynamicFields);
  const actionsReadyRef = useRef(false);

  // Update the ref when dynamicFields changes
  useEffect(() => {
    dynamicFieldsRef.current = dynamicFields;
  }, [dynamicFields]);

  // Get API settings from HavenlyticsData
  const getApiSettings = () => {
    const havenlyticsData = window.HavenlyticsData || {};
    return {
      nonce: havenlyticsData.hvnlyNabApi?.nonce || '',
      url: havenlyticsData.hvnlyNabApi?.url ? `${havenlyticsData.hvnlyNabApi.url}/pb-single-property-builder` : '/wp-json/hvnlynab/v1/pb-single-property-builder'
    };
  };

  // ========== SAVE CONFIGURATION ==========
  const saveConfiguration = useCallback(async () => {
    console.log('💾 Saving Single Property Layout Configuration...');
    
    try {
      setIsLoadingLocal(true);
      setIsLoading(true);
      const apiSettings = getApiSettings();
      
      console.log('📤 Sending data to API:', {
        url: apiSettings.url,
        dynamicFields: dynamicFieldsRef.current
      });

      const response = await fetch(apiSettings.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': apiSettings.nonce
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          dynamicFields: dynamicFieldsRef.current
        })
      });

      console.log('📡 API Response Status:', response.status);

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(__('Permission denied. Cannot save configuration.', 'havenlytics'));
        }
        throw new Error(__('HTTP error! status:', 'havenlytics') + ` ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        showToast('success', __('Success', 'havenlytics'), __('Single property layout saved successfully!', 'havenlytics'));
        console.log('✅ Layout saved successfully:', result);
        return true;
      } else {
        showToast('error', __('Save Failed', 'havenlytics'), __('Failed to save single property layout.', 'havenlytics'));
        console.error('❌ Save failed:', result);
        return false;
      }
    } catch (error) {
      console.error('❌ Save configuration error:', error);
      showToast('error', __('Save Failed', 'havenlytics'), __('Failed to save configuration:', 'havenlytics') + ` ${error.message}`);
      return false;
    } finally {
      setIsLoadingLocal(false);
      setIsLoading(false);
    }
  }, [showToast, setIsLoading]); // Removed dynamicFields from dependencies

  // ========== RESET CONFIGURATION ==========
  const resetConfiguration = useCallback(async () => {
    console.log('🔄 Resetting Single Property Layout to defaults...');
    
    try {
      setIsLoadingLocal(true);
      setIsLoading(true);
      const apiSettings = getApiSettings();
      
      console.log('🗑️ Deleting data from API:', {
        url: apiSettings.url
      });

      const response = await fetch(apiSettings.url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': apiSettings.nonce
        },
        credentials: 'same-origin'
      });

      console.log('📡 API Response Status:', response.status);

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(__('Permission denied. Cannot reset configuration.', 'havenlytics'));
        }
        throw new Error(__('HTTP error! status:', 'havenlytics') + ` ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Reset local state to defaults
        setDynamicFields(initialDynamicFields);
        showToast('success', __('Reset Complete', 'havenlytics'), __('Single property layout reset to defaults!', 'havenlytics'));
        console.log('✅ Layout reset successfully:', result);
        return true;
      } else {
        showToast('error', __('Reset Failed', 'havenlytics'), __('Failed to reset single property layout.', 'havenlytics'));
        return false;
      }
    } catch (error) {
      console.error('❌ Reset configuration error:', error);
      showToast('error', __('Reset Failed', 'havenlytics'), __('Failed to reset configuration:', 'havenlytics') + ` ${error.message}`);
      return false;
    } finally {
      setIsLoadingLocal(false);
      setIsLoading(false);
    }
  }, [showToast, setIsLoading, initialDynamicFields]);

  // ========== LOAD SAVED CONFIGURATION ==========
  const loadSavedConfiguration = useCallback(async () => {
    // Prevent loading if data has already been loaded
    if (dataLoadedRef.current) return;
    
    try {
      console.log('📥 Loading saved single property layout...');
      setIsLoadingLocal(true);
      setIsLoading(true);
      
      const apiSettings = getApiSettings();
      
      console.log('🔍 Fetching data from API:', {
        url: apiSettings.url
      });

      const response = await fetch(apiSettings.url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': apiSettings.nonce
        },
        credentials: 'same-origin'
      });

      console.log('📡 API Response Status:', response.status);

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(__('Permission denied. Cannot load configuration.', 'havenlytics'));
        }
        throw new Error(__('HTTP error! status:', 'havenlytics') + ` ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data && Object.keys(result.data).length > 0) {
        setDynamicFields(result.data);
        console.log('✅ Loaded saved layout:', result.data);
      } else {
        console.log('ℹ️ No saved layout found, using defaults');
      }
      
      // Mark data as loaded
      dataLoadedRef.current = true;
      setIsDataLoaded(true); // Set data loaded state to true
    } catch (error) {
      console.error('❌ Load configuration error:', error);
      // Don't show toast for initial load errors
      setIsDataLoaded(true); // Set data loaded state to true even on error
    } finally {
      setIsLoadingLocal(false);
      setIsLoading(false);
    }
  }, [setIsLoading]);

  // ========== INITIAL LOAD ==========
  useEffect(() => {
    loadSavedConfiguration();
  }, [loadSavedConfiguration]);

  // ========== PASS ACTIONS TO PARENT ==========
  useEffect(() => {
    // Only call onActionsReady once
    if (onActionsReady && !actionsReadyRef.current) {
      actionsReadyRef.current = true;
      onActionsReady({
        saveConfiguration,
        resetConfiguration
      });
    }
  }, [onActionsReady, saveConfiguration, resetConfiguration]);

  // ========== HANDLE PARENT SAVE/RESET ==========
  useEffect(() => {
    // Only call saveConfiguration if isSaving is true and we're not already in the process of saving
    if (isSaving && !isLoadingLocal && !isSavingRef.current) {
      isSavingRef.current = true;
      saveConfiguration().finally(() => {
        isSavingRef.current = false;
      });
    }
  }, [isSaving, isLoadingLocal, saveConfiguration]);

  useEffect(() => {
    // Only call resetConfiguration if isResetting is true and we're not already in the process of resetting
    if (isResetting && !isLoadingLocal && !isResettingRef.current) {
      isResettingRef.current = true;
      resetConfiguration().finally(() => {
        isResettingRef.current = false;
      });
    }
  }, [isResetting, isLoadingLocal, resetConfiguration]);

  // ========== FIELD MANAGEMENT FUNCTIONS ==========
  const addFieldToArea = useCallback((area, fieldType, fieldLabel) => {
    setDynamicFields(prev => {
      // For gallery and agent_info area, replace existing field (only one allowed)
      if (area === 'gallery' || area === 'agent_info') {
        return {
          ...prev,
          [area]: [
            {
              id: `${fieldType}-${Date.now()}`,
              type: fieldType.toLowerCase(),
              label: fieldLabel
            }
          ]
        };
      }
      
      // For other areas, check if field type already exists
      const fieldExists = prev[area].some(field => 
        field.type.toLowerCase() === fieldType.toLowerCase()
      );
      
      if (fieldExists) {
        // If field already exists, don't add it again
        return prev;
      }
      
      // Otherwise, add the new field
      return {
        ...prev,
        [area]: [
          ...prev[area],
          {
            id: `${fieldType}-${Date.now()}`,
            type: fieldType.toLowerCase(),
            label: fieldLabel
          }
        ]
      };
    });
  }, []);

  const removeFieldFromArea = useCallback((area, fieldId) => {
    setDynamicFields(prev => ({
      ...prev,
      [area]: prev[area].filter(field => field.id !== fieldId)
    }));
  }, []);

  const moveFieldUp = useCallback((area, fieldId) => {
    setDynamicFields(prev => {
      const fields = [...prev[area]];
      const index = fields.findIndex(field => field.id === fieldId);
      
      if (index <= 0) return prev;
      
      [fields[index - 1], fields[index]] = [fields[index], fields[index - 1]];
      
      return {
        ...prev,
        [area]: fields
      };
    });
  }, []);

  const moveFieldDown = useCallback((area, fieldId) => {
    setDynamicFields(prev => {
      const fields = [...prev[area]];
      const index = fields.findIndex(field => field.id === fieldId);
      
      if (index >= fields.length - 1) return prev;
      
      [fields[index], fields[index + 1]] = [fields[index + 1], fields[index]];
      
      return {
        ...prev,
        [area]: fields
      };
    });
  }, []);

  const editField = useCallback((field, area) => {
    setEditingField(field);
    setEditingArea(area);
  }, []);

  const saveEditedField = useCallback((updatedField) => {
    if (!editingArea) return;
    
    setDynamicFields(prev => {
      const areaFields = [...prev[editingArea]];
      const fieldIndex = areaFields.findIndex(field => field.id === updatedField.id);
      
      if (fieldIndex === -1) return prev;
      
      areaFields[fieldIndex] = updatedField;
      
      return {
        ...prev,
        [editingArea]: areaFields
      };
    });
    
    setEditingField(null);
    setEditingArea(null);
  }, [editingArea]);

  const closeEditModal = useCallback(() => {
    setEditingField(null);
    setEditingArea(null);
  }, []);

  const openModal = useCallback((area, button) => {
    setActiveModal(area);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const handleFieldOptionClick = useCallback((elementType, area) => {
    // Add the selected element to the area
    addFieldToArea(area, elementType, elementType);
  }, [addFieldToArea]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      closeModal();
      closeEditModal();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [closeModal, closeEditModal]);

  // Modal options for different areas - UPDATED WITH AGENT INFO OPTIONS
  const modalOptions = useMemo(() => ({
    'gallery': [
      { type: 'Carousel', label: __('Image Carousel', 'havenlytics'), icon: 'images', description: __('Add property image carousel = Default', 'havenlytics') },
      { type: 'Map', label: __('Property Map', 'havenlytics'), icon: 'map', description: __('Add interactive property map', 'havenlytics') }
    ],
    'title': [
      { type: 'Title', label: __('Property Title', 'havenlytics'), icon: 'fas fa-heading', description: __('Set the property title (preset field)', 'havenlytics') },
      { type: 'Location', label: __('Property Location', 'havenlytics'), icon: 'fas fa-map-marker-alt', description: __('Add the property address (preset field)', 'havenlytics') },
      { type: 'Rating', label: __('Property Rating', 'havenlytics'), icon: 'fas fa-star', description: __('Display property review stars', 'havenlytics') },
      { type: 'Price', label: __('Property Price', 'havenlytics'), icon: 'fas fa-dollar-sign', description: __('Set property price (preset field)', 'havenlytics') },
    ],

    'tags': [
      { type: 'Share', label: __('Share', 'havenlytics'), icon: 'fas fa-share-alt', description: __('Add social share options', 'havenlytics') },
      { type: 'Departments', label: __('Rent or Sale', 'havenlytics'), icon: 'fas fa-tags', description: __('Mark property as rent or sale', 'havenlytics') },
      { type: 'Labels', label: __('Labels', 'havenlytics'), icon: 'fas fa-tag', description: __('Add labels like "New" or "Hot"', 'havenlytics') },
      { type: 'Tags', label: __('Tags', 'havenlytics'), icon: 'fas fa-tag', description: __('Add property-related tags', 'havenlytics') },
      { type: 'Featured', label: __('Featured', 'havenlytics'), icon: 'fas fa-star', description: __('Mark property as featured', 'havenlytics') },
      { type: 'Popular', label: __('Popular', 'havenlytics'), icon: 'fas fa-fire', description: __('Highlight as popular property', 'havenlytics') },
    ],

    'details': [
      { type: 'Overview', label: __('Property Overview', 'havenlytics'), icon: 'home', description: __('Add property overview', 'havenlytics') },
    ],
    'features': [
      { type: 'Features', label: __('All Features', 'havenlytics'), icon: 'gear', description: __('Add Property Features', 'havenlytics') },
    ],
    // NEW: Agent Info area options
    'agent_info': [
      { type: 'AgentInfo', label: __('Agent Info', 'havenlytics'), icon: 'user-tie', description: __('Display agent contact information and profile', 'havenlytics') },
      { type: 'AgentForm', label: __('Agent Form', 'havenlytics'), icon: 'envelope', description: __('Display contact form for agent inquiries', 'havenlytics') }
    ],
    'amenities': [
      { type: 'Book Property', label: __('Book Property', 'havenlytics'), icon: 'calendar-check', description: __('Allow visitors to book property viewing appointments', 'havenlytics') },
      { type: 'Mortgage Calculator', label: __('Mortgage Calculator', 'havenlytics'), icon: 'calculator', description: __('Add mortgage calculator feature', 'havenlytics') },
      { type: 'Virtual Tour', label: __('Virtual Tour', 'havenlytics'), icon: 'vr-cardboard', description: __('Enable 360° virtual property tours', 'havenlytics') },
      { type: 'Floorplan', label: __('Floorplan', 'havenlytics'), icon: 'project-diagram', description: __('Display interactive property floorplans', 'havenlytics') },
      { type: 'View Brochure', label: __('View Brochure', 'havenlytics'), icon: 'file-pdf', description: __('Show downloadable property brochures', 'havenlytics') },
      { type: 'View EPC', label: __('View EPC', 'havenlytics'), icon: 'chart-line', description: __('Display Energy Performance Certificate', 'havenlytics') },
      { type: 'Property Map', label: __('Property Map', 'havenlytics'), icon: 'map-marked-alt', description: __('Show property location on interactive map', 'havenlytics') },
      { type: 'Request Callback', label: __('Request Callback', 'havenlytics'), icon: 'phone-alt', description: __('Allow visitors to request phone callback', 'havenlytics') },
      { type: 'Arrange Viewing', label: __('Arrange Viewing', 'havenlytics'), icon: 'eye', description: __('Schedule in-person property viewings', 'havenlytics') }
    ],
    'featured': [
      { type: 'Featured Carousel', label: __('Featured Carousel', 'havenlytics'), icon: 'images', description: __('Add featured properties carousel', 'havenlytics') },
    ],
    'views': [
      { type: 'Chart', label: __('Chart', 'havenlytics'), icon: 'chart-bar', description: __('Add daily view chart', 'havenlytics') },
    ]
  }), []);

  // Show placeholder while initial data is loading
  if (!isDataLoaded) {
    return <PlaceholderLoader />;
  }

  return (
    <div className="hvnly--builder--int_api__tab_content_panel">
      <div className="hvnly--builder---api_property__builder__container">
        <PropertyLayout 
          dynamicFields={dynamicFields}
          onOpenModal={openModal}
          onRemoveField={removeFieldFromArea}
          onEditField={editField}
          onMoveFieldUp={moveFieldUp}
          onMoveFieldDown={moveFieldDown}
        />
        
        {/* Field Selection Modal */}
        {Object.keys(modalOptions).map(modalId => (
          <FieldModal
            key={modalId}
            modalId={modalId}
            isActive={activeModal === modalId}
            onClose={closeModal}
            onFieldSelect={handleFieldOptionClick}
            title={__('Insert Element -', 'havenlytics') + ` ${modalId}`}
            options={modalOptions[modalId]}
            usedFields={dynamicFields[modalId] || []}
            // For gallery, don't filter used fields since we allow replacement
            allowReplace={modalId === 'gallery'}
          />
        ))}
        
        {/* Edit Field Modal */}
        <EditFieldModal
          isOpen={!!editingField}
          field={editingField}
          area={editingArea}
          onClose={closeEditModal}
          onSave={saveEditedField}
          modalOptions={modalOptions}
          usedFieldsInArea={editingArea ? dynamicFields[editingArea] || [] : []}
        />
      </div>
    </div>
  );
};

export default SinglePropertyLayoutTab;